const study = {
  meta: {
    slug: "real-estate-dscr-980k-4unit-multifamily-refinance",
    title:
      "$980K DSCR refi: How a portfolio investor got out of hard-money on a four-unit multifamily in 34 days",
    excerpt:
      "An 18-month bridge on a Newark 4-plex was about to reset to a punitive extension rate. The investor needed to refinance into a real long-term product before month 20 — with a DSCR profile the community bank couldn't stomach. Here's the illustrative structure.",
    industry: "Real estate · Multifamily investor",
    product: "DSCR Investment Property Loan (30-yr amortization)",
    amount: "$980K",
    term: "30 years",
    timeToFund: "34 days",
    state: "New Jersey",
    challenge: "Refi out of hard-money",
    image:
      "https://images.pexels.com/photos/210574/pexels-photo-210574.jpeg?auto=compress&cs=tinysrgb&w=1600",
    date: "2026-07-28",
    readTime: "5 min read",
    tags: ["real-estate"],
    relatedArticles: [],
  },
  situation: `A three-property investor with a stable W-2 income and a growing rental portfolio hit the classic hard-money reset wall.

Eighteen months earlier, they'd bought a distressed four-unit multifamily in Newark — $940K purchase, $60K cosmetic rehab, financed 80% with a **hard-money bridge at 11.75% interest-only**. The property stabilized fast: all four units leased at market ($2,850/mo average), gross rent roll of $136,800 annually, operating expenses around $38K.

The bridge lender's contract had an automatic extension clause: if not refinanced by month 20, the rate would step up 300 basis points to **14.75%** — enough to erase the property's cash flow. The investor was at month 18 and starting to sweat.

Two community banks had passed. Neither would underwrite the file against personal DTI (Debt-to-Income) — the investor's three-property portfolio pushed their debt-to-income above the bank's 43% ceiling, even though every unit was cash-flow positive.`,
  structure: `**Why a DSCR loan, not a conventional investment mortgage.** DSCR (Debt Service Coverage Ratio) loans underwrite the *property's* cash flow, not the borrower's personal debt-to-income. For a portfolio investor, this is the difference between "you don't qualify" and "here's your quote."

The math the lender priced against:

- **Gross annual rent**: $136,800
- **Operating expenses** (taxes, insurance, vacancy allowance, capex reserve, property mgmt): ~$52,000
- **Net operating income**: $84,800
- **Proposed annual debt service** at $980K, 30-yr amort, 7.25%: ~$80,200
- **DSCR = $84,800 / $80,200 = 1.06**

At 1.06, the file was tight but bankable. Most DSCR lenders want ≥ 1.20 for prime pricing; anything 1.00–1.19 usually costs a small rate bump. The illustrative structure:

- **$980K DSCR loan**, first-lien on the 4-plex
- **30-year fully amortizing**, fixed 7.25% for the first 5 years, then adjustable
- **75% LTV** on a $1.31M appraisal (the property had appreciated during the 18-month hold)
- **No personal DTI check** — key advantage for portfolio investors
- **Two months of PITI reserves** held at closing
- **Prepay: 5/4/3/2/1** step-down over five years — cheaper than the hard-money exit fee, so this was a net win

**What a savvy broker would NOT do here:** overpromise a lower rate to win the file. DSCR pricing at 1.06 is honest at ~7.25%. A broker quoting 6.50% is either about to hit you with fees at close or is going to fail underwriting and burn your timeline.`,
  outcome: `Application signed on day 3 (after two comparable DSCR quotes were run in parallel). Appraisal ordered day 5, back on day 18. Underwriting cleared day 26. Closing on **day 34** — six days before the hard-money rate reset would have kicked in.

Twelve months later:

- **Monthly cash flow up ~$1,900** vs. the hard-money interest-only structure (thanks to fixed amortization and dramatically lower rate)
- **Property tax-deductible depreciation** on the amortizing loan now flowing through Schedule E
- **Investor added a fifth property** in month 8 — the DSCR file's clean close made subsequent portfolio expansion easier to underwrite

The takeaway: **DSCR products exist for exactly this reason — a personal DTI ceiling should not price a real estate portfolio out of long-term capital.** For any investor holding 3+ doors, understanding DSCR is not optional.`,
};

export default study;
